import dayjs from 'dayjs'

/** Display pattern when the account has none (legacy `date` filter default). */
export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY'

function datePart(value: string): string {
  // Legacy stripped a time part before parsing (`'2026-03-01 10:00:00'` → `'2026-03-01'`).
  return value.trim().split(/[ T]/)[0] ?? ''
}

/**
 * Legacy `date` filter (wp/shell §4.13): the date part of `value` in the account's moment-style pattern (dayjs uses the
 * same tokens). Empty input → `''`; an unparsable value is returned unchanged.
 */
export function formatDate(value: string | Date | null | undefined, pattern: string = DEFAULT_DATE_FORMAT): string {
  if (value === null || value === undefined || value === '') return ''
  const parsed = value instanceof Date ? dayjs(value) : dayjs(datePart(value))
  return parsed.isValid() ? parsed.format(pattern) : String(value)
}

/** Legacy `dateTime` filter: the date pattern plus ` HH:mm`. */
export function formatDateTime(value: string | Date | null | undefined, pattern: string = DEFAULT_DATE_FORMAT): string {
  if (value === null || value === undefined || value === '') return ''
  const parsed = dayjs(value instanceof Date ? value : value.trim().replace(' ', 'T'))
  return parsed.isValid() ? parsed.format(`${pattern} HH:mm`) : String(value)
}

const SERVER_UTC = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}(?::\d{2})?)$/

/**
 * A timestamp the API documents as "YYYY-MM-DD HH:MM:SS, server time in UTC (no offset sent)" — a record's
 * `CreatedAt` / `UpdatedAt` — as an ISO instant (`2026-11-03T12:46:00Z`), so `formatDateTime` shows it in the viewer's
 * time instead of reading the UTC clock as local. A day alone, a value with an offset or anything else is returned as
 * is. Only for fields documented that way: `formatDateTime` itself also gets local wall times (a due date and time).
 */
export function serverUtcTime<T extends string | null | undefined>(value: T): T {
  const match = typeof value === 'string' ? SERVER_UTC.exec(value.trim()) : null
  return (match ? `${match[1]}T${match[2]}Z` : value) as T
}

/** Legacy `time` filter: `HH:mm`. */
export function formatTime(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const clock = typeof value === 'string' ? /^(\d{1,2}):(\d{2})/.exec(value.trim()) : null
  if (clock) return `${clock[1]?.padStart(2, '0')}:${clock[2]}`
  const parsed = dayjs(value instanceof Date ? value : value.trim().replace(' ', 'T'))
  return parsed.isValid() ? parsed.format('HH:mm') : String(value)
}

/** Legacy `fileSize` filter: KB below 1 MB, else MB, two decimals. */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return ''
  const kb = bytes / 1024
  return kb < 1024 ? `${kb.toFixed(2)} KB` : `${(kb / 1024).toFixed(2)} MB`
}

/** Legacy `money` filter: two decimals. */
export function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(value)
  return Number.isFinite(number) ? number.toFixed(2) : String(value)
}

/**
 * Legacy `currency` filter: `Intl.NumberFormat(locale, { style: 'currency', currency })`, EUR and `en-US` by default
 * (08–11 pass the row's currency). A currency code `Intl` refuses (empty, `"-"`, a label) falls back to EUR instead of
 * throwing, and an unknown locale to `en-US`.
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string | null | undefined = 'EUR',
  locale = 'en-US',
): string {
  if (amount === null || amount === undefined || amount === '') return ''
  const number = Number(amount)
  if (!Number.isFinite(number)) return String(amount)
  for (const [code, loc] of [
    [currency || 'EUR', locale],
    ['EUR', locale],
    ['EUR', 'en-US'],
  ] as const) {
    try {
      return new Intl.NumberFormat(loc, { style: 'currency', currency: code }).format(number)
    } catch {
      // RangeError: invalid currency code or locale — try the next fallback.
    }
  }
  return number.toFixed(2)
}

/**
 * HR duration (`wp/hr §5.6`, `TimesheetList.vue:808-819`): total minutes = `hours × 60 + minutes` (non-numbers count
 * as 0, never negative), shown `<h><hUnit> <mm><minUnit>` with the minutes zero-padded — "7h 05min". The units are the
 * translated `common.date.hoursShort` / `minutesShort`; `useFormatters().duration` passes them.
 */
export function formatDuration(
  hours: number | string | null | undefined,
  minutes: number | string | null | undefined = 0,
  units: { hours: string; minutes: string } = { hours: 'h', minutes: 'min' },
): string {
  const toNumber = (value: number | string | null | undefined) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
  }
  const total = Math.max(Math.round(toNumber(hours) * 60 + toNumber(minutes)), 0)
  return `${Math.floor(total / 60)}${units.hours} ${String(total % 60).padStart(2, '0')}${units.minutes}`
}

/**
 * Word truncation (dashboard previews, `ca_Dashboard.vue:263-270`): the first `limit` words and `...` when the text is
 * longer. Works on **text**: legacy split the HTML string and cut tags in half (`wp/dashboards` NEW-13) — callers pass
 * the text content of stored HTML, never the markup.
 */
export function truncateWords(value: string | null | undefined, limit: number): string {
  if (!value) return ''
  const words = value.trim().split(/\s+/)
  return words.length > limit ? `${words.slice(0, limit).join(' ')}...` : value.trim()
}

/** Legacy `capFirst` filter. */
export function capFirst(value: string | null | undefined): string {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/** Legacy `truncate(limit)` filter: cuts at `limit` characters and adds `...`. */
export function truncate(value: string | null | undefined, limit: number): string {
  if (!value) return ''
  return value.length > limit ? `${value.slice(0, limit)}...` : value
}
